import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const pad = (value) => String(value).padStart(2, '0');

/**
 * Minimal re-implementation of Shopify's money_format template replacement,
 * so variant price swaps stay correctly formatted without a page reload.
 */
function formatMoney(cents, format) {
  const value = (cents / 100).toFixed(2);
  const [dollars, decimals] = value.split('.');
  const withThousands = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return format
    .replace(/\{\{\s*amount\s*\}\}/, `${withThousands}.${decimals}`)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withThousands)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${dollars.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimals}`)
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, dollars.replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
}

function initCountdown(root) {
  const container = root.querySelector('[data-raw-countdown]');
  if (!container) return;

  const mode = container.dataset.mode;
  const onExpire = container.dataset.onExpire;
  const durationMinutes = parseFloat(container.dataset.durationMinutes) || 108;
  const promoBar = root.querySelector('.raw-promo-bar');

  const hrsEl = container.querySelector('[data-raw-countdown-hrs]');
  const minEl = container.querySelector('[data-raw-countdown-min]');
  const secEl = container.querySelector('[data-raw-countdown-sec]');

  let endTime;

  if (mode === 'fixed') {
    const parsed = new Date(container.dataset.endDate.replace(' ', 'T'));
    endTime = Number.isNaN(parsed.getTime()) ? Date.now() + durationMinutes * 60000 : parsed.getTime();
  } else {
    endTime = Date.now() + durationMinutes * 60000;
  }

  function tick() {
    let remaining = endTime - Date.now();

    if (remaining <= 0) {
      if (onExpire === 'restart' && mode === 'evergreen') {
        endTime = Date.now() + durationMinutes * 60000;
        remaining = endTime - Date.now();
      } else if (onExpire === 'hide') {
        promoBar?.remove();
        clearInterval(intervalId);
        return;
      } else {
        remaining = 0;
      }
    }

    const totalSeconds = Math.floor(remaining / 1000);
    hrsEl.textContent = pad(Math.floor(totalSeconds / 3600));
    minEl.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
    secEl.textContent = pad(totalSeconds % 60);
  }

  tick();
  const intervalId = setInterval(tick, 1000);
}

function initMenu(root) {
  const toggle = root.querySelector('[data-raw-menu-toggle]');
  const drawer = root.querySelector('[data-raw-menu-drawer]');
  const backdrop = root.querySelector('[data-raw-menu-backdrop]');
  const closeButton = root.querySelector('[data-raw-menu-close]');
  if (!toggle || !drawer || !backdrop) return;

  function open() {
    drawer.hidden = false;
    backdrop.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  function close() {
    drawer.hidden = true;
    backdrop.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  toggle.addEventListener('click', open);
  closeButton?.addEventListener('click', close);
  backdrop.addEventListener('click', close);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !drawer.hidden) close();
  });
}

function initGallery(root) {
  const gallery = root.querySelector('[data-raw-gallery]');
  if (!gallery) return;

  const mainImages = Array.from(gallery.querySelectorAll('.raw-gallery__main-image'));
  const thumbs = Array.from(gallery.querySelectorAll('[data-raw-thumb]'));
  if (mainImages.length === 0) return;

  function keyOf(el) {
    return el.dataset.rawMediaId || el.dataset.rawBlockId;
  }

  function activate(key) {
    mainImages.forEach((img) => img.classList.toggle('is-active', keyOf(img) === key));
    thumbs.forEach((thumb) => thumb.classList.toggle('is-active', keyOf(thumb) === key));
  }

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => activate(keyOf(thumb)));
  });

  function step(delta) {
    const currentIndex = mainImages.findIndex((img) => img.classList.contains('is-active'));
    const nextIndex = (currentIndex + delta + mainImages.length) % mainImages.length;
    activate(keyOf(mainImages[nextIndex]));
  }

  gallery.querySelector('[data-raw-gallery-prev]')?.addEventListener('click', () => step(-1));
  gallery.querySelector('[data-raw-gallery-next]')?.addEventListener('click', () => step(1));

  return { activate, keyOf, mainImages };
}

function initVariants(root, gallery) {
  const form = root.querySelector('[data-raw-product-form]');
  const jsonScript = root.querySelector('[data-raw-product-json]');
  if (!form || !jsonScript) return;

  let productData;
  try {
    productData = JSON.parse(jsonScript.textContent);
  } catch {
    return;
  }

  const selects = Array.from(root.querySelectorAll('[data-raw-option-index]'));
  const variantIdInput = form.querySelector('[data-raw-variant-id]');
  const priceCurrentEl = root.querySelector('[data-raw-price-current]');
  const priceCompareEl = root.querySelector('[data-raw-price-compare]');
  const priceBadgeEl = root.querySelector('[data-raw-price-badge]');
  const cta = root.querySelector('[data-raw-cta]');
  const ctaLabel = root.querySelector('[data-raw-cta-label]');
  const ctaPriceEl = root.querySelector('[data-raw-cta-price]');

  function findMatchingVariant() {
    if (selects.length === 0) return productData.variants[0];

    const selectedOptions = selects.map((select) => select.value);

    return productData.variants.find((variant) =>
      selectedOptions.every((value, index) => variant[`option${index + 1}`] === value)
    );
  }

  function render(variant) {
    if (!variant) return;

    variantIdInput.value = variant.id;

    if (priceCurrentEl) priceCurrentEl.textContent = formatMoney(variant.price, productData.moneyFormat);

    const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (priceCompareEl) {
      priceCompareEl.hidden = !hasDiscount;
      if (hasDiscount) priceCompareEl.textContent = formatMoney(variant.compare_at_price, productData.moneyFormat);
    }

    if (priceBadgeEl) {
      priceBadgeEl.hidden = !hasDiscount;
      if (hasDiscount) {
        const percent = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        priceBadgeEl.textContent = `-${percent}% OFF`;
      }
    }

    if (ctaPriceEl) ctaPriceEl.textContent = formatMoney(variant.price, productData.moneyFormat);

    if (cta) {
      cta.disabled = !variant.available;
      if (ctaLabel && !variant.available) {
        ctaLabel.textContent = cta.dataset.soldOutText;
      } else if (ctaLabel) {
        const priceSuffix = cta.dataset.showPrice === 'true' ? ` · ${formatMoney(variant.price, productData.moneyFormat)}` : '';
        ctaLabel.textContent = `${cta.dataset.addText}${priceSuffix}`;
      }
    }

    if (gallery && variant.featured_media) {
      gallery.activate(String(variant.featured_media.id));
    }
  }

  selects.forEach((select) => {
    select.addEventListener('change', () => render(findMatchingVariant()));
  });
}

function updateCartCount(root, count) {
  const countEl = root.querySelector('[data-raw-cart-count]');
  if (!countEl) return;

  countEl.textContent = String(count);
  countEl.hidden = count === 0;
}

function initCart(root) {
  const form = root.querySelector('[data-raw-product-form]');
  const cta = root.querySelector('[data-raw-cta]');
  const ctaLabel = root.querySelector('[data-raw-cta-label]');
  const errorEl = root.querySelector('[data-raw-cta-error]');
  if (!form || !cta) return;

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (cta.disabled || cta.dataset.loading === 'true') return;

    const originalLabel = ctaLabel.textContent;
    cta.dataset.loading = 'true';
    cta.disabled = true;
    if (ctaLabel) ctaLabel.textContent = cta.dataset.addingText;
    if (errorEl) errorEl.hidden = true;

    const formData = new FormData(form);
    const deferred = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity: Number(formData.get('quantity')) || 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      }).then((res) => res.json());

      if (response.status) {
        throw new Error(response.message || 'No se pudo agregar el producto al carrito.');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((res) => res.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartResponse),
        detail: {
          items: cartResponse.items,
          source: 'custom-product-landing',
          sourceId: root.dataset.sectionId,
          itemCount: cartResponse.item_count,
          productId: root.dataset.sectionId,
          didError: false,
        },
      });

      updateCartCount(root, cartResponse.item_count);

      if (ctaLabel) ctaLabel.textContent = cta.dataset.addedText;
      setTimeout(() => {
        if (ctaLabel) ctaLabel.textContent = originalLabel;
      }, 1800);
    } catch (error) {
      document.dispatchEvent(
        new CartErrorEvent({ error: error.message, code: 'INVALID' })
      );
      deferred.reject(error);

      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = error.message;
      }
      if (ctaLabel) ctaLabel.textContent = originalLabel;
    } finally {
      cta.dataset.loading = 'false';
      cta.disabled = false;
    }
  });
}

function initRoot(root) {
  initCountdown(root);
  initMenu(root);
  const gallery = initGallery(root);
  initVariants(root, gallery);
  initCart(root);
}

document.querySelectorAll('[data-raw-product-root]').forEach(initRoot);
